import { Fragment, type FC, type ReactNode } from 'react';

import { cn } from '@/shared/lib/cn';
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  type ColumnDef,
  type OnChangeFn,
  type Row,
  type SortingState,
} from '@tanstack/react-table';
import { Icon } from '../assets/icons';
import { Loader } from './loader';
import { Pagination, type PaginationProps } from './pagination';
import { Typography } from './typography';

type TableHeaderCellProps = {
  title: string;
  subtitle?: string;
  titleStyle?: string;
  subtitleStyle?: string;
};

export const TableHeaderCell: FC<TableHeaderCellProps> = ({
  title,
  subtitle,
  titleStyle,
  subtitleStyle,
}) => {
  return (
    <div className="text-left">
      <Typography className={cn('font-medium text-gray-800', titleStyle)}>
        {title}
      </Typography>
      <Typography className={cn('text-[12px] text-gray-500', subtitleStyle)}>
        {subtitle}
      </Typography>
    </div>
  );
};

type TableProps<TData> = {
  data?: TData[];
  columns?: ColumnDef<TData>[];
  striped?: boolean;
  firstColSticky?: boolean;
  headerSeparated?: boolean;
  greyHeaders?: boolean;
  className?: string;
  sortingState?: SortingState;
  loading?: boolean;
  emptyState?: boolean;
  errored?: boolean;
  errorMessage?: string;
  pagination?: PaginationProps;
  onSortingChange?: OnChangeFn<SortingState>;
  renderSubComponent?: (props: { row: Row<TData> }) => ReactNode;
  getRowCanExpand?: (row: Row<TData>) => boolean;
  onRowClick?: (row: TData) => void;
  onRowHover?: (row: TData) => void;
  onHoverEnd?: () => void;
};

// TODO: add smoothly transition for opening expanded row using accordion
export const Table = <TData,>({
  data = [],
  columns = [],
  striped = false,
  errored = false,
  errorMessage,
  loading = false,
  emptyState = false,
  firstColSticky = false,
  headerSeparated = false,
  greyHeaders = false,
  sortingState,
  className,
  pagination,
  onSortingChange,
  renderSubComponent,
  getRowCanExpand,
  onRowClick,
  onRowHover,
  onHoverEnd,
}: TableProps<TData>) => {
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting: sortingState,
    },
    onSortingChange,
    getRowCanExpand,
    getExpandedRowModel: getExpandedRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="border-neutral-5 border">
      <div
        className={cn(
          'max-w-full overflow-x-scroll overflow-y-hidden',
          !pagination && 'pb-3'
        )}
      >
        <table
          className={cn('w-full border-separate border-spacing-0', className)}
        >
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, idx) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    style={{
                      width: header.getSize(),
                      minWidth: header.getSize(),
                      maxWidth: header.getSize(),
                    }}
                    className={cn(
                      !header.column.parent ? 'bg-primary-2' : 'bg-neutral-4',
                      'relative p-3',
                      idx === 0 &&
                        firstColSticky &&
                        'after:bg-neutral-7 sticky left-0 z-2 after:pointer-events-none after:absolute after:top-[-1px] after:right-[-1px] after:h-[calc(100%+2px)] after:w-[3px] after:content-[""]',
                      greyHeaders && idx !== 0 && 'bg-neutral-4'
                    )}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div
                      role="button"
                      tabIndex={0}
                      className={cn(
                        'relative',
                        header.column.getCanSort() &&
                          'cursor-pointer pr-4 select-none'
                      )}
                    >
                      {typeof header.column.columnDef.header === 'function' ? (
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )
                      ) : (
                        <Typography
                          noWrap
                          weight="medium"
                          className={'text-neutral-12 relative text-left'}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </Typography>
                      )}

                      <div className="absolute top-1/2 right-0 -translate-y-1/2 text-center">
                        {header.column.getCanSort() &&
                          !header.column.getIsSorted() && (
                            <Icon
                              name="sort-inactive"
                              className="text-gray-500"
                              width={20}
                              height={20}
                            />
                          )}
                        {{
                          asc: (
                            <Icon
                              name="sort-asc"
                              className="text-primary-text"
                              width={20}
                              height={20}
                            />
                          ),
                          desc: (
                            <Icon
                              name="sort-desc"
                              className="text-primary-text"
                              width={20}
                              height={20}
                            />
                          ),
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    </div>

                    {headerSeparated && !header.column.getIsLastColumn() && (
                      <div className="absolute top-1/2 right-0 h-[50%] -translate-y-1/2 border-r border-gray-400" />
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          {!loading && !errored && !emptyState && (
            <tbody onMouseLeave={() => onHoverEnd?.()}>
              {table.getRowModel().rows.map((row, rowIdx) => (
                <Fragment key={row.id}>
                  <tr
                    className={cn(onRowClick && 'cursor-pointer')}
                    onClick={() => onRowClick?.(row.original)}
                    onMouseEnter={() => {
                      onRowHover?.(row.original);
                    }}
                  >
                    {row.getVisibleCells().map((cell, cellIdx) => {
                      return (
                        <td
                          key={cell.id}
                          style={{
                            width: cell.column.getSize(),
                            minWidth: cell.column.getSize(),
                            maxWidth: cell.column.getSize(),
                          }}
                          className={cn(
                            'border-t-neutral-5 border-t border-b-transparent p-3',
                            striped && rowIdx % 2 === 1
                              ? 'bg-neutral-3'
                              : 'bg-neutral-1',
                            firstColSticky &&
                              cellIdx === 0 &&
                              'after:bg-neutral-7 sticky left-0 z-1 after:pointer-events-none after:absolute after:top-[-1px] after:right-[-1px] after:h-[calc(100%+2px)] after:w-[3px] after:content-[""]'
                          )}
                        >
                          {firstColSticky && !cellIdx ? (
                            <Typography
                              noWrap
                              as="span"
                              variant="body"
                              weight="regular"
                              className="text-left"
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </Typography>
                          ) : (
                            flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )
                          )}
                        </td>
                      );
                    })}
                  </tr>

                  {row.getIsExpanded() && (
                    <tr>
                      <td
                        className="bg-white"
                        colSpan={row.getVisibleCells().length}
                      >
                        {renderSubComponent?.({ row })}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          )}
        </table>

        {loading && (
          <div className="flex items-center justify-center py-4">
            <Loader />
          </div>
        )}
        {errored && (
          <div className="flex items-center justify-center py-4">
            <Typography variant="body" className="text-red-600">
              {errorMessage ||
                'An error occurred while loading data. Try again later.'}
            </Typography>
          </div>
        )}
        {emptyState && (
          <div className="flex items-center justify-center py-4">
            <Typography variant="body" className="text-gray-500">
              No data available.
            </Typography>
          </div>
        )}
      </div>
      {pagination && !!data && pagination.totalPages > 1 && (
        <div className="flex w-full items-center justify-center pt-3 pb-2">
          <Pagination {...pagination} />
        </div>
      )}
    </div>
  );
};
